import React, { useState } from 'react';
import type { Link, LinkStats } from '../../services/linksService';
import { relativeTime } from '../../utils/time';
import { copyText } from '../dealroom/dealLinkModel';

/** Said once wherever click counts are shown. Clicks are the only thing a link can see. */
export const CLICKS_HONESTY = 'Anonymous clicks — not buyers, not views. Spoken or typed URLs aren’t counted.';

const DEVICES: Array<[keyof LinkStats['by_device'], string]> = [
  ['mobile', 'Phone'], ['desktop', 'Computer'], ['tablet', 'Tablet'], ['other', 'Other'],
];

/** Everything a link's click log holds: counts, device class, referring sites, countries.
 *  Never who clicked — the log has no one to show. */
export const LinkStatsDetail: React.FC<{ stats: LinkStats }> = ({ stats }) => {
  if (stats.total_clicks === 0) {
    return <p className="br-small">No clicks yet.</p>;
  }
  return (
    <div className="grid gap-2.5">
      <p className="br-small">
        <strong className="br-num" style={{ color: 'var(--ink)' }}>{stats.total_clicks.toLocaleString('en-US')}</strong> all time
        {' · '}<strong className="br-num" style={{ color: 'var(--ink)' }}>{stats.clicks_7d.toLocaleString('en-US')}</strong> in 7 days
        {' · '}<strong className="br-num" style={{ color: 'var(--ink)' }}>{stats.clicks_30d.toLocaleString('en-US')}</strong> in 30 days
        {stats.last_click_at && <> · last click {relativeTime(stats.last_click_at, { rolloverDays: 7 })}</>}
      </p>
      <div>
        <p className="br-caption">Devices</p>
        <p className="br-small">
          {DEVICES.filter(([k]) => stats.by_device[k] > 0).map(([k, name]) => `${name} ${stats.by_device[k].toLocaleString('en-US')}`).join(' · ')}
        </p>
      </div>
      <div>
        <p className="br-caption">Where clicks came from</p>
        {stats.top_referrers.length > 0 ? (
          <p className="br-small break-words">
            {stats.top_referrers.map((r) => `${r.host} ${r.clicks.toLocaleString('en-US')}`).join(' · ')}
          </p>
        ) : (
          <p className="br-small">No referring site recorded — apps and typed links often send none.</p>
        )}
      </div>
      {stats.top_countries.length > 0 && (
        <div>
          <p className="br-caption">Countries</p>
          <p className="br-small">{stats.top_countries.map((c) => `${c.country} ${c.clicks.toLocaleString('en-US')}`).join(' · ')}</p>
        </div>
      )}
    </div>
  );
};

/**
 * One Bearri link on a ruled list: its name, the short URL with Copy, where it
 * forwards, its 30-day clicks, and the switches. The parent list is `br-ruled`.
 */
const LinkRow: React.FC<{
  link: Link;
  /** Dashboard: say which deal the link belongs to. */
  showDeal?: boolean;
  /** Deal Room: the click detail starts open. */
  defaultOpen?: boolean;
  onToggleActive?: (link: Link) => Promise<void>;
  onRetire?: (link: Link) => Promise<void>;
}> = ({ link, showDeal = false, defaultOpen = false, onToggleActive, onRetire }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState(false);
  const run = async (fn?: (l: Link) => Promise<void>) => {
    if (!fn || busy) return;
    setBusy(true);
    try { await fn(link); } catch { /* the handler says what went wrong */ } finally { setBusy(false); }
  };
  const name = link.label || link.destination_host;
  return (
    <div className="py-3.5" data-testid="link-row">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="br-h3 min-w-0 break-words">{name}</span>
            {showDeal && link.brand_name && <span className="br-tag br-tag--sm">Deal: {link.brand_name}</span>}
            {!link.active && <span className="br-count br-count--amber">Paused</span>}
          </div>
          <div className="mt-1.5 flex min-w-0 items-center gap-2">
            <span className="br-mono min-w-0 truncate" title={link.url}>{link.url.replace(/^https?:\/\//, '')}</span>
            <button type="button" onClick={() => copyText(link.url)} className="br-slab br-slab--white br-slab--sm flex-none"
              aria-label={`Copy ${link.url}`}>
              Copy
            </button>
          </div>
          <p className="br-caption mt-1 truncate" title={link.destination_url}>Forwards to {link.destination_url}</p>
        </div>
        <div className="flex-none text-right">
          <div className="br-num text-[15px]" style={{ color: 'var(--ink)' }}>{link.stats.clicks_30d.toLocaleString('en-US')}</div>
          <div className="br-caption">{link.stats.clicks_30d === 1 ? 'click' : 'clicks'} · 30 days</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="br-inkline text-caption">
          {open ? 'Hide click detail' : 'Click detail'}
        </button>
        {onToggleActive && (
          <button type="button" disabled={busy} onClick={() => run(onToggleActive)} className="br-caption font-semibold underline-offset-2 hover:underline"
            style={{ color: 'var(--ink-2)' }}>
            {link.active ? 'Pause' : 'Turn back on'}
          </button>
        )}
        {onRetire && (
          <button type="button" disabled={busy}
            onClick={() => { if (window.confirm('Remove this link? It stops forwarding, and anyone who clicks it sees “This link isn’t active”.')) run(onRetire); }}
            className="br-caption font-semibold underline-offset-2 hover:underline" style={{ color: 'var(--ink-2)' }}>
            Remove
          </button>
        )}
      </div>
      {open && <div className="br-sheet br-sheet--plain mt-2 p-3"><LinkStatsDetail stats={link.stats} /></div>}
    </div>
  );
};

export default LinkRow;
